import "../scss/admin_navbar.scss";

import { CContainer, CNavbar } from "@coreui/react";
import React, {
  Suspense,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { Can } from "src/helpers/Auth";
import PropTypes from "prop-types";
// routes config
import { Settings } from "@mui/icons-material";
import routes from "../routes";
import { isFeatureEnabled } from "src/config/featureFlags";
import { isInternalUser } from "src/helpers/internalUser";

const loading = (
  <div className="pt-3 text-center">
    <div className="sk-spinner sk-spinner-pulse" />
  </div>
);

const TheAdminNavbar = (props) => {
  // styled navbar riding right under breadcrumbs and above the main content that will allow for links to admin settings

  return (
    <CNavbar
      expand="lg"
      className="admin-toolbar"
      style={{ margin: "0 0 8px 0" }}
    >
      <CContainer fluid>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Settings style={{ color: "#6c757d" }} />
          <div style={{ color: "#6c757d" }}>
            {props.routeName} Administration
          </div>
        </div>
        {props.children}
      </CContainer>
    </CNavbar>
  );
};

//validate props
TheAdminNavbar.propTypes = {
  children: PropTypes.node,
  routeName: PropTypes.string,
};

const TheContent = () => {
  const targetRef = useRef();
  const [height, setHeight] = useState(800);
  const internalUser = isInternalUser();

  // Filter routes by feature flag
  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      // If no feature flag is set, include the route
      if (!route.featureFlag) return true;
      // Otherwise check if the feature is enabled
      return isFeatureEnabled(route.featureFlag);
    });
  }, []);

  // Collect and pass height of the main content window with responsive updates
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (targetRef.current) {
        setHeight(targetRef.current.offsetHeight);
      }
    };

    // Initial measurement
    updateHeight();

    // Update on window resize
    window.addEventListener("resize", updateHeight);

    // Cleanup
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <main
      className="c-main main-app"
      ref={targetRef}
      style={{
        paddingTop: "0px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CContainer
        fluid
        style={{
          padding: 0,
          height: "100%",
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Suspense fallback={loading}>
          <Switch>
            {filteredRoutes.map((route, idx) => {
              let routeHasAdminComponent = route.adminComponent;
              return (
                route.component && (
                  <Route
                    key={idx}
                    path={route.path}
                    exact={route.exact}
                    name={route.name}
                    render={(props) => (
                      <div
                        style={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {routeHasAdminComponent && (
                          // only show the admin bar to admins
                          <Can
                            perform={"admin"}
                            key={idx}
                            yes={() => (
                              <TheAdminNavbar {...props} routeName={route.name}>
                                <route.adminComponent {...props} />
                              </TheAdminNavbar>
                            )}
                            no={() => {
                              return <></>;
                            }}
                          />
                        )}
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {route._internalOnly && !internalUser ? (
                            // Nuclearn-only page: hiding the sidebar entry is
                            // not enough, direct navigation has to bounce too.
                            // Kept separate from the `_roles` check below
                            // because `Can` lets any admin through.
                            <Redirect to="/" />
                          ) : route._roles ? (
                            // Gate the whole route by role. The sidebar only
                            // hides menu items; without this, any authenticated
                            // user could reach an admin route by direct
                            // navigation. Non-matching users are redirected.
                            <Can
                              perform={route._roles}
                              product={route._product}
                              yes={() => (
                                <route.component {...props} height={height} />
                              )}
                              no={() => <Redirect to="/" />}
                            />
                          ) : (
                            <route.component {...props} height={height} />
                          )}
                        </div>
                      </div>
                    )}
                  />
                )
              );
            })}
            <Redirect exact from="/admin/groups" to="/users?tab=groups" />
            <Redirect from="/" to="/assistant" />
          </Switch>
        </Suspense>
      </CContainer>
    </main>
  );
};

export default React.memo(TheContent);
